import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";

import type { ClientsOptionsActionPreference } from "../../../../helpers/clientsListContext";

type ClientsOptionsMenuProps = {
  guideUrl: string;
  lastAction: ClientsOptionsActionPreference | null;
  onRememberAction: (action: ClientsOptionsActionPreference) => void;
  onImport: () => void;
  onMerge: () => void;
};

const ClientsOptionsMenu = ({
  guideUrl,
  lastAction,
  onRememberAction,
  onImport,
  onMerge,
}: ClientsOptionsMenuProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const recentBadge = (action: ClientsOptionsActionPreference) =>
    lastAction === action ? (
      <span className="clients-options-menu__recent text-muted small ms-2">
        {t("clients.options.recent")}
      </span>
    ) : null;

  const closeAndRemember = (action: ClientsOptionsActionPreference, run: () => void) => {
    setOpen(false);
    onRememberAction(action);
    run();
  };

  const quickMeta: { label: string; icon: string } | null = (() => {
    switch (lastAction) {
      case "import":
        return { label: t("clients.import.button"), icon: "ri-upload-2-line" };
      case "merge":
        return { label: t("clients.merge.button"), icon: "ri-merge-cells-horizontal" };
      case "guide":
        return { label: t("help.articleLink"), icon: "ri-book-open-line" };
      default:
        return null;
    }
  })();

  return (
    <div className="clients-options-menu d-flex align-items-center gap-1">
      {lastAction === "import" && quickMeta ? (
        <Button
          color="secondary"
          outline
          size="sm"
          type="button"
          className="clients-options-menu__quick bg-white text-body border-secondary-subtle"
          onClick={() => {
            onRememberAction("import");
            onImport();
          }}
          title={quickMeta.label}
        >
          <i className={`${quickMeta.icon} align-middle me-1`} aria-hidden="true" />
          <span className="clients-options-menu__quick-label">{quickMeta.label}</span>
        </Button>
      ) : null}
      {lastAction === "merge" && quickMeta ? (
        <Button
          color="secondary"
          outline
          size="sm"
          type="button"
          className="clients-options-menu__quick bg-white text-body border-secondary-subtle"
          onClick={() => {
            onRememberAction("merge");
            onMerge();
          }}
          title={quickMeta.label}
        >
          <i className={`${quickMeta.icon} align-middle me-1`} aria-hidden="true" />
          <span className="clients-options-menu__quick-label">{quickMeta.label}</span>
        </Button>
      ) : null}
      {lastAction === "guide" && quickMeta ? (
        <Button
          color="secondary"
          outline
          size="sm"
          tag="a"
          href={guideUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="clients-options-menu__quick bg-white text-body border-secondary-subtle"
          onClick={() => onRememberAction("guide")}
          title={quickMeta.label}
        >
          <i className={`${quickMeta.icon} align-middle me-1`} aria-hidden="true" />
          <span className="clients-options-menu__quick-label">{quickMeta.label}</span>
        </Button>
      ) : null}
      <Dropdown
        isOpen={open}
        toggle={() => setOpen((current) => !current)}
        className="clients-options-menu__dropdown"
      >
        <DropdownToggle
          color="secondary"
          outline
          size="sm"
          className="clients-options-menu__toggle bg-white text-body border-secondary-subtle"
          caret
        >
          <i className="ri-settings-3-line align-middle me-1" aria-hidden="true" />
          {t("clients.options.label")}
        </DropdownToggle>
        <DropdownMenu end container="body" positionFixed>
          <DropdownItem onClick={() => closeAndRemember("import", onImport)}>
            <i className="ri-upload-2-line align-middle me-1" aria-hidden="true" />
            {t("clients.import.button")}
            {recentBadge("import")}
          </DropdownItem>
          <DropdownItem onClick={() => closeAndRemember("merge", onMerge)}>
            <i className="ri-merge-cells-horizontal align-middle me-1" aria-hidden="true" />
            {t("clients.merge.button")}
            {recentBadge("merge")}
          </DropdownItem>
          <DropdownItem
            tag="a"
            href={guideUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => closeAndRemember("guide", () => undefined)}
          >
            <i className="ri-book-open-line align-middle me-1" aria-hidden="true" />
            {t("help.articleLink")}
            {recentBadge("guide")}
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default ClientsOptionsMenu;
